import React, { useState, useEffect, useMemo } from "react";
import { entities, getDisplayMessage } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Trash2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import EditalCard from "../components/historico/EditalCard";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function HistoricoPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [editaisSelecionados, setEditaisSelecionados] = useState([]);
  const [dialogExclusaoAberto, setDialogExclusaoAberto] = useState(false);
  const [comentarioExclusao, setComentarioExclusao] = useState("");
  const [trashPreviewLoading, setTrashPreviewLoading] = useState(false);
  const [trashPreview, setTrashPreview] = useState(null);

  const canDeleteEditais = user?.role === "admin" || user?.perfil === "admin" || user?.permissoes?.permitir_exclusao_edital !== false;

  const { data: editais = [], isLoading, error } = useQuery({
    queryKey: ['editais-historico'],
    queryFn: async () => {
      try {
        // view=historico: listagem leve (sem itens/checklist/dados_extraidos) — evita payload enorme
        const data = await entities.Edital.list("-createdAt", 100, { view: "historico" });
        return data || [];
      } catch (error) {
        console.error("Erro ao carregar editais:", error);
        throw new Error("Não foi possível carregar os editais. Por favor, tente novamente.");
      }
    },
    retry: 2,
    staleTime: 30000,
  });

  useEffect(() => {
    if (!dialogExclusaoAberto || editaisSelecionados.length === 0) {
      setTrashPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setTrashPreviewLoading(true);
      setTrashPreview(null);
      try {
        const data = await entities.Edital.trashPreview(editaisSelecionados);
        if (cancelled) return;
        const editais = data?.editais ?? [];
        const sum = {
          propostas: 0,
          contratos: 0,
          licoesAprendidas: 0,
          agendaEvents: 0,
          atestadosDeclaracoes: 0,
        };
        let anyBlocked = false;
        let anyMissing = false;
        for (const e of editais) {
          if (e.missing) {
            anyMissing = true;
            continue;
          }
          if (e.blocked) anyBlocked = true;
          sum.propostas += e.counts?.propostas ?? 0;
          sum.contratos += e.counts?.contratos ?? 0;
          sum.licoesAprendidas += e.counts?.licoesAprendidas ?? 0;
          sum.agendaEvents += e.counts?.agendaEvents ?? 0;
          sum.atestadosDeclaracoes += e.counts?.atestadosDeclaracoes ?? 0;
        }
        setTrashPreview({ editais, anyBlocked, anyMissing, sum });
      } catch (err) {
        if (!cancelled) {
          setTrashPreview({
            error: true,
            message: getDisplayMessage(err, "Não foi possível verificar vínculos. Tente novamente."),
          });
        }
      } finally {
        if (!cancelled) setTrashPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dialogExclusaoAberto, editaisSelecionados]);

  const naoHaEditaisValidosParaExcluir = useMemo(() => {
    if (!trashPreview?.editais || trashPreview.error || trashPreviewLoading) return false;
    return editaisSelecionados.every((id) => {
      const row = trashPreview.editais.find((entry) => entry.id === id);
      return !row || row.missing;
    });
  }, [trashPreview, trashPreviewLoading, editaisSelecionados]);

  const deleteEditaisMutation = useMutation({
    mutationFn: async ({ ids, cascade, motivo_exclusao }) => {
      await Promise.all(
        ids.map((id) => entities.Edital.delete(id, { cascade: !!cascade, motivo_exclusao }))
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['editais-historico'] });
      queryClient.invalidateQueries({ queryKey: ['editais'] });
      queryClient.invalidateQueries({ queryKey: ['editais-analise'] });
      queryClient.invalidateQueries({ queryKey: ['editais-licitacao'] });
      queryClient.invalidateQueries({ queryKey: ['editais-status-report'] });
      setEditaisSelecionados([]);
      setComentarioExclusao("");
      setDialogExclusaoAberto(false);
    },
    onError: (err) => {
      console.error("Erro ao excluir editais:", err);
      toast.error(getDisplayMessage(err, "Erro ao excluir editais. Tente novamente."), { duration: 5000 });
    }
  });

  const filteredEditais = editais.filter(edital =>
    edital.nome_arquivo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    edital.titulo_licitacao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    edital.instituicao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    edital.numero_processo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    edital.objeto?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleView = (edital) => {
    navigate(createPageUrl("Resultados") + `?id=${edital.id}`);
  };

  const handleToggleSelecao = (editalId) => {
    setEditaisSelecionados(prev =>
      prev.includes(editalId)
        ? prev.filter(id => id !== editalId)
        : [...prev, editalId]
    );
  };

  const handleSelecionarTodos = () => {
    if (editaisSelecionados.length === filteredEditais.length) {
      setEditaisSelecionados([]);
    } else {
      setEditaisSelecionados(filteredEditais.map(e => e.id));
    }
  };

  const handleExcluirSelecionados = () => {
    setComentarioExclusao("");
    setDialogExclusaoAberto(true);
  };

  const handleConfirmarExclusao = (e) => {
    e?.preventDefault?.();
    const motivo = comentarioExclusao.trim();
    if (!motivo) {
      toast.error("Informe o motivo da exclusão para manter o histórico claro.");
      return;
    }
    if (trashPreviewLoading || trashPreview?.error) return;
    const idsToDelete =
      trashPreview?.editais && !trashPreview.error
        ? editaisSelecionados.filter((id) => {
            const row = trashPreview.editais.find((entry) => entry.id === id);
            return row && !row.missing;
          })
        : editaisSelecionados;
    if (idsToDelete.length === 0) {
      toast.error("Nenhum edital disponível para exclusão.");
      return;
    }
    const cascade = trashPreview?.anyBlocked === true;
    deleteEditaisMutation.mutate({ ids: idsToDelete, cascade, motivo_exclusao: motivo });
  };

  if (error) {
    return (
      <div className="p-4 md:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error.message || "Erro ao carregar dados. Por favor, recarregue a página."}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 min-h-screen" data-tour="tour-Historico-root">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Histórico de Editais</h1>
          <p className="text-slate-600">Todos os editais processados</p>
        </div>

        <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm mb-6" data-tour="tour-Historico-primary">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  placeholder="Buscar por nome, instituição, processo ou objeto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-12 text-base border-slate-200 focus:border-blue-500"
                />
              </div>

              {canDeleteEditais && filteredEditais.length > 0 && (
                <div className="flex items-center gap-3 w-full md:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelecionarTodos}
                    className="whitespace-nowrap"
                  >
                    {editaisSelecionados.length === filteredEditais.length ? "Desmarcar Todos" : "Selecionar Todos"}
                  </Button>
                  {editaisSelecionados.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleExcluirSelecionados}
                      className="whitespace-nowrap"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Excluir ({editaisSelecionados.length})
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4">
          {isLoading ? (
            Array(5).fill(0).map((_, i) => (
              <Card key={i} className="border-none shadow-md animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))
          ) : filteredEditais.length === 0 ? (
            <Card className="border-none shadow-md">
              <CardContent className="p-12 text-center">
                <p className="text-slate-500 text-lg">Nenhum edital encontrado</p>
              </CardContent>
            </Card>
          ) : (
            filteredEditais.map((edital) => (
              <EditalCard
                key={edital.id}
                edital={edital}
                onView={handleView}
                isSelected={canDeleteEditais ? editaisSelecionados.includes(edital.id) : undefined}
                onToggleSelection={canDeleteEditais ? handleToggleSelecao : undefined}
              />
            ))
          )}
        </div>
      </div>

      <AlertDialog
        open={dialogExclusaoAberto}
        onOpenChange={(open) => {
          setDialogExclusaoAberto(open);
          if (!open) setComentarioExclusao("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Os {editaisSelecionados.length} edital(is) selecionado(s) serão movidos para a lixeira (podem ser
                  recuperados pelo administrador dentro do prazo de retenção).
                </p>
                {trashPreviewLoading && <p className="text-foreground">Verificando vínculos…</p>}
                {trashPreview?.error && (
                  <p className="text-destructive">{trashPreview.message}</p>
                )}
                {trashPreview && !trashPreview.error && !trashPreviewLoading && (
                  <>
                    {trashPreview.anyMissing && (
                      <p className="text-amber-700 dark:text-amber-500">
                        Alguns itens não foram encontrados ou já não estão disponíveis; eles serão ignorados na
                        exclusão.
                      </p>
                    )}
                    {trashPreview.anyBlocked && (
                      <p className="text-foreground">
                        Existem propostas, contratos ou lições aprendidas vinculados. Ao confirmar, esses registros
                        também serão enviados à lixeira. Eventos da agenda e declarações perderão o vínculo com o
                        edital.
                        <span className="mt-2 block text-sm">
                          Totais: {trashPreview.sum.propostas} proposta(s), {trashPreview.sum.contratos}{" "}
                          contrato(s), {trashPreview.sum.licoesAprendidas} lição(ões) aprendida(s);{" "}
                          {trashPreview.sum.agendaEvents} evento(s) de agenda e{" "}
                          {trashPreview.sum.atestadosDeclaracoes} declaração(ões) serão desvinculados.
                        </span>
                      </p>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 px-1">
            <Label htmlFor="historico-motivo-exclusao">Motivo da exclusão (obrigatório)</Label>
            <Textarea
              id="historico-motivo-exclusao"
              placeholder="Descreva o motivo para manter o histórico claro..."
              value={comentarioExclusao}
              onChange={(e) => setComentarioExclusao(e.target.value)}
              className="min-h-[100px]"
              disabled={deleteEditaisMutation.isPending}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteEditaisMutation.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarExclusao}
              disabled={
                deleteEditaisMutation.isPending ||
                trashPreviewLoading ||
                Boolean(trashPreview?.error) ||
                naoHaEditaisValidosParaExcluir ||
                !comentarioExclusao.trim()
              }
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteEditaisMutation.isPending ? "Excluindo…" : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
